import { Alert, Button, Panel } from '@ixicare/ui';
import * as React from 'react';
import styled from 'styled-components';
import { t } from '~/i18n';
import GeofenceMap from '../components/GeofenceMap';
import GeofenceMapWrapper from '../components/GeofenceMapWrapper';
import ZoneSaveChoiceModal from '../components/ZoneSaveChoiceModal';
import GeofenceSettingsContainer from './GeofenceSettingsContainer';
import { useZones } from '~hooks/zone';
import { useZonePresetSelection } from '~modules/zone-presets/useZonePresetSelection';
import type { GeofenceSelection } from '~modules/zone-presets/useZonePresetSelection';
import { IndoorGeofencingMode } from '@ixicare/resources';

// Who the zones are being picked for. Drives the wording alone - every subject is saved the same
// way and held to the same rules.
export type ZoneSubject = 'alarmSender' | 'group' | 'preset';

interface GeofenceCreatorContainerProps {
  siteUuid: string;
  // The presets endpoints key on the site's numeric id, not its uuid.
  siteId?: number;
  // What these zones belong to. Only an alarm sender's own geofence reads as allow/block: a group's
  // zones say which alarms reach it, and a preset is a named list either of them can follow.
  subject: ZoneSubject;
  // Which way the selection reads, for an alarm sender only. Absent elsewhere: nothing to invert.
  mode?: IndoorGeofencingMode;
  selectedZones: string[];
  zonePresetId?: string | null;
  onSave: (selection: GeofenceSelection) => void;
  onCancel: () => void;
}

const GeofenceCreatorContainer: React.FC<GeofenceCreatorContainerProps> = ({
  siteUuid,
  siteId,
  subject,
  mode,
  selectedZones,
  zonePresetId,
  onSave,
  onCancel,
}) => {
  const [selectedPlane, setSelectedPlane] = React.useState<string>();
  const [newSelection, setNewSelection] = React.useState<string[]>(selectedZones);

  // Every zone of the whole site, not just the level on screen: the per-level "select all" cannot
  // see the other levels, and this is the only place that holds the full selection.
  const allZones = useZones({ siteUuid });

  const preset = useZonePresetSelection({
    siteId,
    mode,
    initialPresetId: zonePresetId ?? null,
    selection: newSelection,
    setSelection: setNewSelection,
    onSave,
  });

  // The copy is the only thing the subject changes: an alarm sender's list reads as allowed or
  // disallowed zones, the other two read as neither.
  const copy = subject === 'alarmSender' ? mode : subject;

  const allZonesSelected = allZones.length > 0 && allZones.every((zone) => newSelection.includes(zone.uuid));

  // Only an alarm sender's own geofence is meaningless when it covers the whole site: as an allow
  // list it restricts nothing, as a block list it leaves the wearer nowhere to be, and the API
  // refuses both. A group covering everything is a real configuration - an overview group for
  // administration is meant to see every alarm - and a preset is the list such groups share, so
  // both are warned rather than refused. A preset needs no rule of its own: picking one here loads
  // its zones into the selection, so an alarm sender reaching for a full-site preset trips this
  // very check.
  const allZonesBlocked = allZonesSelected && subject === 'alarmSender';

  const handleSave = () => {
    if (allZonesBlocked) return;
    preset.save();
  };

  return (
    <>
      <Panel.Subtitle>{t(`geofence.creator.${copy}.title`)}</Panel.Subtitle>
      <GeofenceMapWrapper>
        <GeofenceMap
          siteId={siteUuid}
          onPlaneChange={setSelectedPlane}
          selectedPlane={selectedPlane}
          onZonesChange={setNewSelection}
          selectedZones={newSelection}
          isSelectable={true}
        />
        <GeofenceSettingsContainer
          siteUuid={siteUuid}
          selectedPlane={selectedPlane}
          onPlaneChange={setSelectedPlane}
          onZoneSelectChange={setNewSelection}
          show={true}
          selectedZones={newSelection}
          presetOptions={preset.options}
          presetValue={preset.value}
          onPresetChange={preset.choose}
        />
      </GeofenceMapWrapper>
      {allZonesSelected && (
        <Notice>
          <Alert type={allZonesBlocked ? 'error' : 'warning'}>{t(`geofence.creator.${copy}.allZonesSelected`)}</Alert>
        </Notice>
      )}
      {preset.onBlockList && (
        <Notice>
          <Alert type="error">{t('geofence.preset.blockList')}</Alert>
        </Notice>
      )}

      <ButtonRow>
        <Button onClick={onCancel} buttonType="link">
          {t('global.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={allZonesBlocked || preset.onBlockList}>
          {t('geofence.settings.submit')}
        </Button>
      </ButtonRow>

      {preset.choice && <ZoneSaveChoiceModal {...preset.choice} />}
    </>
  );
};

const Notice = styled.div`
  margin: 12px 0;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

export default GeofenceCreatorContainer;
